import { z } from 'zod';

import { zGoSlice } from '../helpers/fix-go-types';

const CostTierSchema = z.object({
  case_cost: z.number(),
  min_cases: z.number(),
});

// year_month is a packed YYYYMM integer (e.g. 202604), not a string —
// dateutils.YearMonth.MarshalJSON. Confirmed in the Task 5 handler test.
const WindowLowSchema = z.object({
  cost: z.number(),
  year_month: z.number(),
});

const CostHistoryEntrySchema = z.object({
  year_month: z.number(),
  frontline_cost: z.number(),
  lowest_achievable: z.number(),
  // `Tiers models.CostTiers` carries no omitempty (api_cost_history.go:26), so a
  // month with no tier schedule arrives as null rather than an empty array.
  tiers: zGoSlice(CostTierSchema),
});

const WindowBoolsSchema = z.object({
  '6': z.boolean(),
  '12': z.boolean(),
  '24': z.boolean(),
});

const WindowLowsSchema = z.object({
  '6': WindowLowSchema,
  '12': WindowLowSchema,
  '24': WindowLowSchema,
});

// recurrence / cycle_period_months / next_month_lowest_achievable are intentionally
// omitted — unused by the UI. Zod strips unknown keys by default, so this is safe
// even when the backend serializes next_month_lowest_achievable as literal null.
const CostSummarySchema = z.object({
  current_month_lowest_achievable: z.number(),
  is_lowest_in_window: WindowBoolsSchema,
  lowest_in_window: WindowLowsSchema,
  // `MonthsSinceLastLow *int` with no omitempty (models/product_cost_history.go:62)
  // — present as literal null when the product has never been at a window low.
  months_since_last_low: z.number().nullable().optional(),
});

export const CostHistoryResponseSchema = z.object({
  vendor: z.string(),
  // `Series []CostHistoryEntry` carries no omitempty (api_cost_history.go:32).
  series: zGoSlice(CostHistoryEntrySchema),
  summary: CostSummarySchema.optional(),
});

export type CostHistoryResponse = z.infer<typeof CostHistoryResponseSchema>;
export type CostHistoryEntry = z.infer<typeof CostHistoryEntrySchema>;
